import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useQuickView } from "../context/QuickViewContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useProductCopy } from "../hooks/useProductCopy";
import { ShoppingBag, Heart, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export const QuickViewModal = () => {
    const { t } = useTranslation();
    const { product, isOpen, close } = useQuickView();
    const { addItem } = useCart();
    const { has, toggle } = useWishlist();
    const [size, setSize] = useState(null);
    const localized = useProductCopy(product);

    useEffect(() => {
        if (!isOpen) setSize(null);
    }, [isOpen]);

    if (!product) return null;
    const liked = has(product.id);

    const onAdd = () => {
        if (!size) {
            toast.error(t("product.select_size_first"));
            return;
        }
        addItem(product, size);
        toast.success(t("product.added_toast", { name: localized.name }));
        close();
    };

    return (
        <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
            <DialogContent
                data-testid="quick-view-modal"
                className="max-w-4xl w-[92vw] sm:w-full p-0 bg-[#0C0C0C] border border-white/10 text-[#F4F4F0] rounded-none"
            >
                <DialogTitle className="sr-only">{localized.name}</DialogTitle>
                <DialogDescription className="sr-only">{localized.name}</DialogDescription>
                <div className="grid sm:grid-cols-2">
                    <div className="relative aspect-[4/5] sm:aspect-auto bg-[#141414] overflow-hidden">
                        <img src={product.images[0]} alt={localized.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="p-6 sm:p-8 flex flex-col gap-5">
                        <div>
                            <div className="text-overline text-[#A3A3A3]">{product.category} · {product.season}</div>
                            <h2 className="text-display text-3xl sm:text-4xl mt-2 leading-[0.95]">{localized.name}</h2>
                            <div className="text-brutal text-lg mt-3">${product.price}</div>
                        </div>
                        <p className="text-[#A3A3A3] text-sm leading-relaxed line-clamp-3">{localized.description}</p>

                        <div className="space-y-2">
                            <span className="text-overline text-[#A3A3A3]">{t("product.select_size")}</span>
                            <div className="grid grid-cols-5 gap-2">
                                {product.sizes.map((s) => (
                                    <button
                                        key={s}
                                        data-testid={`qv-size-${s}`}
                                        onClick={() => setSize(s)}
                                        className={`py-2.5 text-[10px] tracking-[0.18em] uppercase font-display transition-all border ${
                                            size === s
                                                ? "bg-[#EDEDE6] text-[#0C0C0C] border-[#EDEDE6]"
                                                : "border-white/15 text-[#F4F4F0] hover:border-white/40"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex gap-2 mt-auto">
                            <button onClick={onAdd} className="btn-primary flex-1" data-testid="qv-add-to-cart">
                                <ShoppingBag className="h-4 w-4" strokeWidth={1.5} /> {t("product.add_to_cart")}
                            </button>
                            <button
                                onClick={() => toggle(product)}
                                data-testid="qv-wishlist-toggle"
                                aria-label={t("product.toggle_wishlist")}
                                className="px-4 border border-white/15 hover:border-white transition-colors"
                            >
                                <Heart className={`h-4 w-4 ${liked ? "fill-[#F4F4F0]" : ""}`} strokeWidth={1.4} />
                            </button>
                        </div>
                        <Link
                            to={`/product/${product.slug}`}
                            onClick={close}
                            className="text-overline text-[#A3A3A3] hover:text-[#F4F4F0] inline-flex items-center gap-2"
                            data-testid="qv-view-details"
                        >
                            {t("home.view_all")} <ArrowRight className="h-3 w-3" />
                        </Link>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
